import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Camera, CheckCircle2, Loader2, Plus, RefreshCw } from "lucide-react";
import { themeApi } from "./api";
import type { ThemeTemplate, ThemeTemplateStatus } from "./types";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";
import { useFontCatalog } from "./useFontCatalog";
import { TemplateEditor } from "./editor/TemplateEditor";
import { cn } from "@/lib/utils/cn";

const STATUS_LABEL: Record<ThemeTemplateStatus, string> = {
  draft: "Borrador",
  published: "Publicado",
  active: "Activo",
};

const STATUS_TONE: Record<ThemeTemplateStatus, string> = {
  draft: "nx-badge-neutral",
  published: "nx-badge-info",
  active: "nx-badge-success",
};

export function ThemeScreen() {
  // Precarga Google Fonts (~70 fuentes en 1 request) para preview de cada opción.
  useFontCatalog();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const qc = useQueryClient();
  const { push } = useToast();
  const canEditDraft = usePermission(PERMISSIONS.THEME_EDIT_DRAFT);

  const listQuery = useQuery({
    queryKey: ["theme", "templates"],
    queryFn: () => themeApi.list(),
  });

  const templates = listQuery.data ?? [];

  // Auto-select: si no hay seleccionada, elegir la activa o la primera
  useEffect(() => {
    if (templates.length === 0) {
      setSelectedId(null);
      return;
    }
    if (selectedId === null || !templates.find((t) => t.id === selectedId)) {
      const active = templates.find((t) => t.status === "active");
      setSelectedId((active || templates[0]).id);
    }
  }, [templates, selectedId]);

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const snapshot = useMutation({
    mutationFn: (name: string) => themeApi.snapshot(name, false),
    onSuccess: (tpl) => {
      push({ kind: "success", message: `"${tpl.name}" creada como borrador.` });
      qc.invalidateQueries({ queryKey: ["theme", "templates"] });
      setSelectedId(tpl.id);
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al crear." }),
  });

  const handleCreate = async () => {
    const name = await confirmAction({
      title: "Nueva plantilla",
      message:
        "Crea un borrador con un snapshot del tema activo actualmente en producción. Lo podés editar libremente.",
      promptLabel: "Nombre de la plantilla",
      confirmLabel: "Crear borrador",
    });
    if (name !== null && (name as string).trim()) snapshot.mutate((name as string).trim());
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Tema</p>
          <h1 className="nx-h1">Tema del portal mixcore.club</h1>
          <p className="text-sm text-fg-muted mt-1">
            Biblioteca de plantillas con modo oscuro y claro. Editá libremente
            y aplicá cuando esté listo.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          disabled={listQuery.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {/* Las plantillas están desconectadas del portal público desde el
          rediseño 1A (julio 2026). Sin este aviso, editar y aplicar una
          plantilla parece funcionar pero no cambia nada en mixcore.club, y no
          hay forma de darse cuenta desde acá. */}
      <div className="p-3 rounded-md border border-warning/40 bg-warning-soft flex gap-3">
        <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
        <div className="text-sm text-fg-muted">
          <p className="font-semibold text-warning mb-1">
            Las plantillas no se están aplicando en mixcore.club
          </p>
          <p>
            El portal público usa el rediseño con su propia paleta y su propio
            claro/oscuro, así que ignora la plantilla Activa. Aplicar una acá no
            cambia nada de lo que ve el usuario final.
          </p>
          <p className="mt-1.5">
            El <strong className="text-fg">preview navegable</strong> sí sigue
            funcionando: sirve para revisar una plantilla antes de publicarla.
            Los portales internos (hub, staff, nexus, artist, partner) tampoco
            se ven afectados.
          </p>
          <p className="mt-1.5 text-xs text-fg-subtle">
            Para reactivarlas hay que poner{" "}
            <code className="font-mono">THEME_TEMPLATES_ENABLED = true</code> en{" "}
            <code className="font-mono">
              mixcore-web · src/Componentes/Theme/themeTemplates.js
            </code>
            .
          </p>
        </div>
      </div>

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando plantillas…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : templates.length === 0 ? (
        <div className="nx-card p-8 text-center">
          <p className="text-sm text-fg-muted mb-3">
            Aún no hay plantillas guardadas
            {canEditDraft
              ? ". Creá la primera a partir del tema actual."
              : ". Pedile al Owner o a un Admin que cree la primera."}
          </p>
          {canEditDraft && (
            <button onClick={handleCreate} className="nx-btn-primary">
              {snapshot.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Camera size={14} />
              )}
              Snapshot tema actual
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start">
          {/* Sidebar de plantillas */}
          <aside className="nx-card p-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold">
                Plantillas ({templates.length})
              </h2>
              {canEditDraft && (
                <button
                  onClick={handleCreate}
                  disabled={snapshot.isPending}
                  className="nx-btn-secondary text-2xs px-2 py-1"
                  title="Crear nueva (snapshot del activo)"
                >
                  {snapshot.isPending ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Plus size={11} />
                  )}
                  Nueva
                </button>
              )}
            </div>
            <p className="text-2xs text-fg-subtle mb-3 leading-relaxed">
              Editá cualquier plantilla libremente. Cuando una te convenza, hacé{" "}
              <strong className="text-fg-muted">Aplicar a producción</strong>.
            </p>
            <div className="flex flex-col gap-1.5">
              {templates.map((tpl) => (
                <TemplateRow
                  key={tpl.id}
                  tpl={tpl}
                  selected={tpl.id === selectedId}
                  onSelect={() => setSelectedId(tpl.id)}
                />
              ))}
            </div>
          </aside>

          {/* Editor principal */}
          <main>
            {selected ? (
              <TemplateEditor
                key={selected.id}
                template={selected}
                onDeleted={() => setSelectedId(null)}
              />
            ) : (
              <div className="nx-card p-8 text-center text-fg-subtle text-sm">
                Seleccioná una plantilla.
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
}

function TemplateRow({
  tpl,
  selected,
  onSelect,
}: {
  tpl: ThemeTemplate;
  selected: boolean;
  onSelect: () => void;
}) {
  const authorLabel = formatCreator(tpl.createdBy);
  return (
    <button
      onClick={onSelect}
      className={cn(
        "text-left rounded-md px-2.5 py-2 transition-colors border",
        selected
          ? "bg-accent-soft border-accent"
          : "border-transparent hover:bg-bg-hover",
      )}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="text-xs font-medium text-fg truncate flex-1">{tpl.name}</span>
        {tpl.status === "active" && <CheckCircle2 size={12} className="text-success" />}
      </div>
      <div className="flex items-center gap-1.5">
        <span className={STATUS_TONE[tpl.status]}>{STATUS_LABEL[tpl.status]}</span>
        <span className="text-[10px] text-fg-subtle truncate">
          {formatDateTime(tpl.updatedAt)}
        </span>
      </div>
      {authorLabel && (
        <div className="text-[10px] text-fg-subtle mt-0.5 truncate">
          Por {authorLabel}
        </div>
      )}
    </button>
  );
}

function formatCreator(creator: ThemeTemplate["createdBy"]): string | null {
  if (!creator) return null;
  const full = [creator.firstName, creator.lastName].filter(Boolean).join(" ").trim();
  return full || creator.email;
}
